import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { APP_LINKS, bannerLink, isAppWebviewPage, smartAppBannerContent } from "./applinks.mjs";
import { isArabic, readStorage, writeStorage } from "./browserstate";
import "./applinks.css";

// "Open in the Cinescape app", for phones where Safari's own Smart App Banner
// does not appear. docs/architecture/app-links-tracking-journeys.md §6.
// Which link it carries on which phone is decided in applinks.mjs, and
// whether it shows at all by bannerEnabled in applinks.config.mjs.

const DISMISSED_AT = "appBannerDismissedAt";
const DISMISS_FOR_MS = 14 * 24 * 60 * 60 * 1000;
const IN_APP_WEBVIEW = "insideCinescapeApp";

// While the banner is up, <html> carries this class and the banner's height,
// so the site's own buttons fixed to the bottom of the screen can sit above
// it rather than under it (applinks.css).
const BANNER_SHOWN_CLASS = "applinks-banner-shown";
const BANNER_HEIGHT_VAR = "--applinks-banner-h";

const TEXT = {
  en: {
    title: "Cinescape",
    subtitle: "Open in the Cinescape app",
    open: "Open",
    close: "Close",
    hint: "If nothing happens, tap ••• and choose Open in browser",
  },
  ar: {
    title: "سينسكيب",
    subtitle: "افتح في تطبيق سينسكيب",
    open: "افتح",
    close: "إغلاق",
    hint: "إذا لم يحدث شيء، اضغط ••• واختر الفتح في المتصفح",
  },
};

function dismissedRecently() {
  const at = Number(readStorage("localStorage", DISMISSED_AT));
  const age = Date.now() - at;
  return at > 0 && age >= 0 && age < DISMISS_FOR_MS;
}

// The apps open one of their own pages (/app/tnc and the like) in a web view.
// Anything after that in the same tab is still inside the app, wherever its
// links lead, so this is remembered for the tab rather than read per page.
function insideAppWebview(firstPath) {
  if (isAppWebviewPage(firstPath)) writeStorage("sessionStorage", IN_APP_WEBVIEW, "1");
  return readStorage("sessionStorage", IN_APP_WEBVIEW) === "1";
}

// Safari reads the Smart App Banner tag once, at load, and index.html writes
// it then. After that the address changes without a load, so app-argument is
// kept in step here. A page that must not offer the app leaves the last
// address in place, and the tag is never added after load.
function useSmartAppBannerArgument(location, config) {
  useEffect(() => {
    const meta = document.querySelector('meta[name="apple-itunes-app"]');
    const content = smartAppBannerContent(window.location, config);
    if (meta && content) meta.setAttribute("content", content);
  }, [location.pathname, location.search, config]);
}

// Tells the page the banner is up, and how tall it is now: the hint line, an
// Arabic subtitle or the phone's home-indicator area all change that.
function useBannerSpace(bannerRef, shown, hint) {
  useLayoutEffect(() => {
    const banner = bannerRef.current;
    if (!shown || !banner) return undefined;
    const root = document.documentElement;
    const measure = () => {
      const height = Math.ceil(banner.getBoundingClientRect().height);
      if (height > 0) root.style.setProperty(BANNER_HEIGHT_VAR, height + "px");
    };
    root.classList.add(BANNER_SHOWN_CLASS);
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(banner);
    return () => {
      if (observer) observer.disconnect();
      root.classList.remove(BANNER_SHOWN_CLASS);
      root.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, [bannerRef, shown, hint]);
}

function AppBanner({ config = APP_LINKS }) {
  const location = useLocation();
  const bannerRef = useRef(null);
  const [hidden, setHidden] = useState(
    () => insideAppWebview(window.location.pathname) || dismissedRecently()
  );
  useSmartAppBannerArgument(location, config);

  const link = hidden ? null : bannerLink(window.location, window.navigator, config);
  useBannerSpace(bannerRef, !!link, !!(link && link.hint));
  if (!link) return null;

  const text = isArabic() ? TEXT.ar : TEXT.en;
  const dismiss = () => {
    writeStorage("localStorage", DISMISSED_AT, String(Date.now()));
    setHidden(true);
  };

  return (
    <>
      <div
        className={"applinks-banner-spacer" + (link.hint ? " applinks-banner-spacer-hint" : "")}
        aria-hidden="true"
      />
      <div className="applinks-banner" role="region" aria-label={text.subtitle} ref={bannerRef}>
        <span className="applinks-banner-icon" aria-hidden="true">
          <img src={new URL("../../assets/logo-mobile.png", import.meta.url).href} alt="" />
        </span>
        <div className="applinks-banner-text">
          <p className="applinks-banner-title">{text.title}</p>
          <p className="applinks-banner-subtitle">{text.subtitle}</p>
          {link.hint ? <p className="applinks-banner-hint">{text.hint}</p> : null}
        </div>
        <a className="applinks-banner-open" href={link.href}>
          {text.open}
        </a>
        <button
          type="button"
          className="applinks-banner-close"
          aria-label={text.close}
          onClick={dismiss}
        >
          ×
        </button>
      </div>
    </>
  );
}

export default AppBanner;
